// E2b'nin saf mantığı (spec §2, §3, §5). Ekranda hesap yok: sayaç, örnek ve hata sayısı API'den gelir.
import type { PreviewResponse, ProfileFormat, Sample, UploadMapping, ValueMap, RowError } from "../../shared/api";
import { formatAuditTime } from "../../shared/audit";
import { ENUMS, UNITS, parseNumber, type NumberFormat } from "../../shared/normalize";
import { SOURCE_TYPES, type SourceType } from "../../shared/rows";
import { EVENT_DATE_FIELD, FIELDS, type BoxName } from "../../shared/sources";
import { formatChange, formatDate, formatNumber } from "../../shared/versions";
import { ApiFailure, previewUpload } from "./client";

type RowErrorItem = RowError;

/** `GG.AA.YYYY` (İstanbul günü). */
export const formatDay = (iso: string) => formatAuditTime(iso).slice(0, 10);

export const BOX_LABEL: Record<BoxName, string> = { customs: "GÜMRÜK", erp: "ERP", accounting: "MUHASEBE", security: "GÜVENLİK" };

export const fieldLabel = (f: string) => FIELDS[f]?.label ?? f;

/** Değer eşleme hedefleri (ADR-0012: İngilizce enum) ve ekran etiketleri. */
const VALUE_LABEL: Record<string, string> = {
  goods_receipt: "Giriş", goods_issue: "Çıkış", consumption: "Sarf", production: "Üretim", transfer: "Transfer", shipment: "Sevk",
  vehicle: "Araç", staff: "Personel", visitor: "Ziyaretçi", in: "Giriş", out: "Çıkış", imports: "İthalat", exports: "İhracat",
};
/** E6 alan değeri: sayı `tr-TR`, kategorik değer Türkçe etiket, tarih GG.AA.YYYY, boş "—". Ham kod (`goods_receipt`) ekrana çıkmaz. */
export function displayValue(sourceType: string, field: string, v: string | number | null): string {
  if (v === null) return "—";
  if (typeof v === "number") return formatNumber(v);
  const kind = sourceType in SOURCE_TYPES ? (SOURCE_TYPES[sourceType as SourceType].kinds as Record<string, string>)[field] : undefined;
  return kind === "date" ? formatDate(v) : field in ENUMS ? (VALUE_LABEL[v] ?? v) : v;
}
/** Fark satırı: iki sayı arasında `formatChange` (artı/eksi farkıyla), diğerlerinde `displayValue → displayValue`. */
export const changeText = (sourceType: string, field: string, old: string | number | null, now: string | number | null) =>
  typeof old === "number" && typeof now === "number" ? formatChange(old, now) : `${displayValue(sourceType, field, old)} → ${displayValue(sourceType, field, now)}`;
export const valueOptions = (field: string): { value: string; label: string }[] =>
  [...new Set(Object.values((ENUMS as Record<string, Record<string, string>>)[field] ?? {}))].map((v) => ({ value: v, label: VALUE_LABEL[v] ?? v }));

/** E2c birim listesi: normalize edilen birimler (KG, ADET, MT, M2). `UNITS` anahtarları takma addır, kullanılmaz (ui.md §11.3). */
export const unitOptions = (): string[] => [...new Set(Object.values(UNITS).map((u) => u.unit))];

/** E2c koşullu/varsayılan alan cümlesi (ui.md §11.3), anahtar `kaynakTipi.alan`. Koşulun kendisi `SOURCE_TYPES[t].conditional`'dan okunur. */
export const FIELD_HINT: Record<string, string> = {
  "ERP_STOK.referenceDoc": "Hareket Tipi Giriş ya da Sevk ise zorunlu.",
  "ERP_STOK.workOrderNo": "Hareket Tipi Sarf ya da Üretim ise zorunlu.",
  "GUVENLIK.plate": "Kayıt Tipi Araç ise zorunlu.",
  "GUMRUK_IHR.itemNo": "Boş bırakılırsa 1 kabul edilir.",
  "MUHASEBE.voucherNo": "Satır sırasını sistem verir: bu fişin son satırından sonraki sıra.",
};

export const FORMAT_LABEL: Record<NumberFormat, string> = { tr: "TR (1.234,56)", en: "EN (1,234.56)", plain: "Düz (1234.56)" };

export const SOURCE_TYPE_CODES = Object.keys(SOURCE_TYPES) as SourceType[];

/** Kullanıcının o ana kadarki seçimleri. API önizlemede etkin eşlemeyi geri verir; taslak ona eşitlenir. */
export type Draft = {
  sheet?: string; sourceType?: SourceType; mapping: Record<string, string>; format: ProfileFormat; valueMap: ValueMap; profileId?: number;
  /** ADR-0018: kullanıcının seçtiği ya da önerilen dönem (yazılmakta olanda boş uç olabilir). `full`: "Tam hâl". */
  period?: { start: string; end: string }; full?: boolean;
};
export const emptyDraft: Draft = { mapping: {}, format: {}, valueMap: {} };

/**
 * Önizleme yanıtından taslak. `auto`: istek eşleme taşımıyordu (dosya, sayfa ya da kaynak tipi seçimi),
 * yani yanıttaki eşleme profilden ya da öneriden geldi. Eşleşen profil aynı kaynak tipindeyse taslak profildir.
 */
export function draftFrom(d: Draft, p: PreviewResponse, auto: boolean): Draft {
  const fromProfile = auto && !!p.matchedProfile && p.matchedProfile.sourceType === p.sourceType;
  return {
    sheet: p.sheet, sourceType: p.sourceType ?? undefined, mapping: p.mapping, format: p.format, valueMap: p.valueMap,
    profileId: fromProfile ? p.matchedProfile!.profileId : auto ? undefined : d.profileId,
    period: d.period, full: d.full,
  };
}

/** Tarihli tip (dönem alabilir). RECETE, GTIP_MALZEME, DIIB her zaman tam hâldir (ADR-0018). */
export const isDated = (t: string | undefined): boolean => !!t && t in EVENT_DATE_FIELD;

/** Dönem alanının hatası; geçerliyse null. Tam hâlde ve tarihsiz tipte hata yok. */
export function periodError(d: Draft): string | null {
  if (!isDated(d.sourceType) || d.full || !d.period) return null;
  const { start, end } = d.period;
  if (!start || !end) return "Dönem başlangıcı ve bitişi gerekli";
  return start > end ? "Dönem başlangıcı bitişten sonra olamaz" : null;
}
/**
 * Önizleme isteği (ADR-0018 kullanıcı kararı): tarihli tipte `period` yokken API önerilen dönemi verir; taslak onu alır
 * ve önizleme dönemle yenilenir (taşınacak ve kaldırılacak sayılar dönemle hesaplanır). "Tam hâl" öneriyi uygulamaz.
 */
export async function previewWithPeriod(f: File, d: Draft, mapping: UploadMapping | undefined): Promise<{ draft: Draft; preview: PreviewResponse }> {
  const first = await previewUpload(f, { sheet: d.sheet, mapping, period: periodOf(d) });
  if (!first.suggestedPeriod || d.full || d.period) return { draft: d, preview: first };
  const draft = { ...d, period: first.suggestedPeriod };
  return { draft, preview: await previewUpload(f, { sheet: d.sheet, mapping, period: draft.period }) };
}

/** İstekteki `period`: yalnız tarihli tipte, "Tam hâl" değilken ve geçerliyken gönderilir. */
export const periodOf = (d: Draft) => (isDated(d.sourceType) && !d.full && d.period && !periodError(d) ? d.period : undefined);

/** İstek `mapping` alanı. Profil değişmediyse yalnız `profileId`, değiştiyse alan → kolon eşlemesi. */
export function toMapping(d: Draft): UploadMapping {
  if (d.profileId !== undefined) return { sourceType: d.sourceType, profileId: d.profileId };
  return { sourceType: d.sourceType, mapping: d.mapping, format: d.format, valueMap: d.valueMap };
}

/** Yükle düğmesi: zorunlu alanlar tamam, açık soru ve eşlenmemiş değer yok. */
export const canUpload = (p: PreviewResponse | null, d: Draft): boolean =>
  !periodError(d) && !!p && !!d.sourceType && !!p.required && p.required.matched === p.required.total && p.questions.length === 0 && p.unknownValues.length === 0;

/** "Yükle" pasifken nedeni (#292); yüklenebilirse null. Sıra kullanıcının yapacağı sıradır. */
export function uploadBlocker(p: PreviewResponse | null, d: Draft, needJustification: boolean, hasFile: boolean): string | null {
  if (!hasFile) return "Dosya seçilmedi";
  if (!p) return null; // önizleme yükleniyor; düğmeyi "meşgul" durumu pasifleştirir
  if (!d.sourceType) return "Kaynak tipi seçilmedi";
  if (p.required && p.required.matched < p.required.total) return counterText(p.required);
  if (p.questions.length) return "Sayı biçimi seçilmedi";
  if (p.unknownValues.length) return "Tanınmayan değerler eşlenmedi";
  const pe = periodError(d);
  if (pe) return pe;
  return needJustification ? "Gerekçe gerekli" : null;
}

/** Gerekçe kutusu: önizleme "gerekçe şart" diyorsa yüklemeden ÖNCE, ya da API 422 JUSTIFICATION_REQUIRED döndüyse. */
export const needsJustification = (p: PreviewResponse | null, f: Failure | null): boolean =>
  !!p?.changes?.justificationRequired || f?.kind === "justification";

// Sayının iyelik eki: 6'sı, 3'ü, 5'i, 10'u… (0-99)
const ONES = ["ı", "i", "si", "ü", "ü", "i", "sı", "si", "i", "u"]; // 0 → sıfırı
const TENS = ["", "u", "si", "u", "ı", "si", "ı", "i", "i", "ı"]; // 10, 20, … 90
const suffix = (n: number) => (n % 10 ? ONES[n % 10]! : n === 0 ? "ı" : TENS[n / 10]!);
export const counterText = (r: { matched: number; total: number }) => `${r.total} zorunlu alanın ${r.matched}'${suffix(r.matched)} eşleşti`;

/** ADR-0013: profil adı tekildir; düzeltme yeni ad ister. "ERP stok" → "ERP stok 2"; alınmışsa sıradaki boş sayı. */
export function suggestProfileName(base: string, taken: string[]): string {
  const root = base.replace(/\s+\d+$/, "");
  const used = new Set(taken);
  let n = 2;
  while (used.has(`${root} ${n}`)) n++;
  return `${root} ${n}`;
}

/** Örnek hücresi: ham değer (dosyadaki) → okunan değer (ekran biçimiyle: "117.000,00 → 117.000", "2026-03-12" → "12.03.2026"); okunamazsa nedeniyle. */
export function sampleText(s: Sample | undefined, sourceType = ""): { raw: string; normalized: string | null; problem?: string } | null {
  if (!s) return null;
  return { raw: s.raw === null ? "(boş)" : String(s.raw), normalized: s.normalized === null ? null : displayValue(sourceType, s.field, s.normalized), problem: s.problem };
}

/** "117.000 = 117000 mi, 117 mi?" (spec §3): seçeneklerin verdiği farklı okumalar. */
export function questionText(q: { sample: string; options: NumberFormat[] }): string {
  const reads = [...new Set(q.options.map((o) => parseNumber(q.sample, o)).filter((r) => r.ok).map((r) => (r as { value: number }).value))];
  return `${q.sample} = ${reads.map((v) => `${v} mi`).join(", ")}?`;
}

export const REASON_LABEL: Record<string, string> = {
  INVALID_NUMBER: "Sayı biçime uymuyor", INVALID_DATE: "Tarih geçersiz ya da biçime uymuyor", INVALID_TIME: "Saat geçersiz",
  UNKNOWN_UNIT: "Bilinmeyen birim", REQUIRED_EMPTY: "Zorunlu alan boş", INVALID_TEXT: "Metin alanı metin ya da sayı değil",
  DUPLICATE_KEY: "Dosyada tekrar eden kayıt kimliği", OUTSIDE_PERIOD: "Tarih seçilen dönemin dışında", UNKNOWN_VALUE: "Geçersiz değer",
};
export const reasonLabel = (r: string) => REASON_LABEL[r] ?? r;

/** Yükleme denemesinin ekrana yansıyan sonucu. */
export type Failure =
  | { kind: "already"; uploadId: number }
  | { kind: "rows"; items: RowErrorItem[]; total: number }
  | { kind: "justification"; changedCount: number; removedCount: number }
  | { kind: "refresh"; text: string } // soru ya da eksik alan: önizleme yenilenir
  | { kind: "text"; text: string };

export function toFailure(e: unknown, fallback: (e: unknown) => string): Failure {
  if (!(e instanceof ApiFailure)) return { kind: "text", text: fallback(e) };
  const b = e.body;
  switch (b.code) {
    case "FILE_ALREADY_UPLOADED": return { kind: "already", uploadId: Number(b.uploadId) };
    case "ROW_ERRORS": {
      const items = (b.rowErrors as RowErrorItem[] | undefined) ?? [];
      return { kind: "rows", items, total: typeof b.totalErrors === "number" ? b.totalErrors : items.length };
    }
    case "JUSTIFICATION_REQUIRED": return { kind: "justification", changedCount: Number(b.changedCount ?? 0), removedCount: Number(b.removedCount ?? 0) };
    case "AMBIGUOUS_FORMAT": case "VALUE_MAP_MISSING": case "REQUIRED_FIELDS_MISSING": return { kind: "refresh", text: b.message };
    case "FILE_TOO_LARGE": return { kind: "text", text: "Dosya 25 MB sınırını aşıyor." };
    default: return { kind: "text", text: fallback(e) };
  }
}
